import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link2, Unplug } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/States";

export default function QuickBooksCard() {
  const { data, loading, reload } = useFetch("/qbo/status");
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const r = params.get("qbo");
    if (!r) return;
    if (r === "connected") toast.success("QuickBooks connected"); else toast.error(r === "denied" ? "QuickBooks connection was cancelled" : "QuickBooks connection failed");
    params.delete("qbo"); setParams(params, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy(true);
    try { const { data: d } = await api.get("/qbo/connect"); window.location.href = d.authorize_url; } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/qbo/disconnect"); toast.success("QuickBooks disconnected"); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border bg-card p-6" data-testid="qbo-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow">Integrations</p>
          <h3 className="mt-2 flex items-center gap-2 section-title">QuickBooks Online
            {loading ? null : data?.connected ? <Badge data-testid="qbo-status-badge">Connected</Badge> : data?.configured ? <Badge variant="outline" data-testid="qbo-status-badge">Not connected</Badge> : <Badge variant="secondary" data-testid="qbo-status-badge">Coming soon</Badge>}
          </h3>
          <p className="mt-2 max-w-lg text-sm text-muted-foreground" data-testid="qbo-description">
            {data?.connected ? <>Connected to <strong>{data.company_name || `company ${data.realm_id}`}</strong> ({data.environment}). Send any saved estimate to QuickBooks as an estimate or invoice from the project's Estimate tab.</>
              : data?.configured ? "Push approved estimates straight into QuickBooks as estimates or invoices. Customers are matched by name or created for you."
              : "Push approved estimates straight into QuickBooks as estimates or invoices. The platform is finishing its Intuit app setup; the Connect button will light up here as soon as it's ready."}
          </p>
        </div>
        {loading ? <Spinner /> : data?.connected
          ? <Button variant="outline" className="rounded-full" onClick={disconnect} disabled={busy} data-testid="qbo-disconnect-button">{busy ? <Spinner /> : <Unplug className="mr-2 h-4 w-4" />}Disconnect</Button>
          : <Button className="rounded-full" onClick={connect} disabled={busy || !data?.configured} data-testid="qbo-connect-button">{busy ? <Spinner /> : <Link2 className="mr-2 h-4 w-4" />}Connect QuickBooks</Button>}
      </div>
    </section>
  );
}
